import { memo, useMemo, useEffect, useState, FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { isNil, isEmpty } from 'lodash';
// Import Services
import { getMatchedProperties, mapPropertiesToCascadeDropdownModel } from 'services/customerJourney/helpers';
import { Helpers } from 'services';
import { UtilsHttpService } from 'services/http';
// Import Components
import DraggableForm from 'components/DraggableForm';
import CJBestMessageDeliveryMethodRow from './CJBestMessageDeliveryMethodRow';
import DynamicVariableOption from '../DynamicVariableOption';
// Import UI Components
import { SkeletonLoader } from '@geneui/components';
// Import Helpers
import { getInitialDeliveryMethodConfig } from './helpers';
// Import Types
import { DeliveryMethodDataMap, DependencyObject, DndSortedDataItem, CJDynamicVariables } from './types';
import { AvailableDeliveryMethodItem } from 'types';
// Import Hooks
import useRequest from 'hooks/UseRequest';
import useCancelableRequests from 'hooks/UseCancelableRequests';
// Import Styles
import styles from './CJBestMessageSender.module.scss';

const { accumulateConfigFromStates } = Helpers;

interface CJBestMessageSenderProps {
    getUpdate: (data: { deliveryMethodValues; sortedArray }) => void;
    option: AnyObject;
    node: AnyObject;
    elements: AnyObject[];
    updateDependency: (data: DependencyObject) => void;
    defaultValue?: DeliveryMethodDataMap;
    collectedProperties?: AnyObject[];
}

const CJBestMessageSender: FC<CJBestMessageSenderProps> = ({
    defaultValue = {},
    getUpdate,
    option,
    elements,
    collectedProperties,
    node,
    updateDependency,
}) => {
    const { t } = useTranslation();

    const [isLoadingDynamicVariables, setIsLoadingDynamicVariables] = useState(false);
    const [isLoadingDeliveryMethods, setIsLoadingDeliveryMethods] = useState(true);
    const [availableDeliveryMethods, setAvailableDeliveryMethods] = useState<AvailableDeliveryMethodItem[]>([]);
    const [deliveryMethodValuesMap, setDeliveryMethodValuesMap] = useState<DeliveryMethodDataMap>(defaultValue);
    const [dndSortedConfigData, setDndSortedConfigData] = useState<DndSortedDataItem[]>([]);

    const { doGetRequest } = useRequest();

    const { getAvailableDeliveryMethodsRequest } = useCancelableRequests({
        getAvailableDeliveryMethodsRequest: UtilsHttpService.getAvailableDeliveryMethods,
    });

    const handleDraggableFormChange = useCallback((data) => {
        setDndSortedConfigData(data);
    }, []);

    const deliveryMethodConfigs = useMemo(
        () => {
            if (isEmpty(availableDeliveryMethods)) return [];
            return getInitialDeliveryMethodConfig({
                option,
                availableDeliveryMethods,
                defaultValue,
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [option, availableDeliveryMethods], // Removed defaultValue from deps intentionally as new value is received after every getUpdate
    );

    useEffect(() => {
        setDndSortedConfigData(deliveryMethodConfigs);
    }, [deliveryMethodConfigs]);

    useEffect(() => {
        // Adds the new channels that weren't present during last save
        if (isNil(dndSortedConfigData)) {
            return;
        }
        setDeliveryMethodValuesMap((prevMap) => {
            const newMap = { ...prevMap };
            let hasChanged = false;

            dndSortedConfigData.forEach(({ config }) => {
                if (!newMap[config.deliveryMethodId]) {
                    newMap[config.deliveryMethodId] = {
                        isEnabled: false,
                        template: null,
                        provider: null,
                        dynamicVariables: {} as AnyObject,
                        deliveryMethod: config.deliveryMethodId,
                    };
                    hasChanged = true;
                }
            });

            return hasChanged ? newMap : prevMap;
        });
    }, [dndSortedConfigData]);

    const handleGetUpdate = useCallback(() => {
        if (!getUpdate) return;

        const filteredObject = Object.fromEntries(
            Object.entries(deliveryMethodValuesMap).filter(([_, value]) => value.isEnabled),
        );
        getUpdate({ deliveryMethodValues: filteredObject, sortedArray: dndSortedConfigData });
    }, [deliveryMethodValuesMap, dndSortedConfigData, getUpdate]);

    useEffect(handleGetUpdate, [handleGetUpdate]);

    const dynamicVariablesChangeHandler = (fieldName, newValue) => {
        if (isNil(newValue) || newValue.isValid !== undefined) return;

        setDeliveryMethodValuesMap((prevMap) => {
            const updatedMap = JSON.parse(JSON.stringify(prevMap));
            for (const key in updatedMap) {
                const config = updatedMap[key];
                if (config.dynamicVariables?.[fieldName]) {
                    config.dynamicVariables[fieldName].value = newValue.value;
                    config.dynamicVariables[fieldName].isChecked = newValue.isChecked;
                }
            }

            return updatedMap;
        });
    };

    const dynamicVariablesValidationHandler = (fieldName, validation) => {
        if (isNil(validation) || validation.isValid === undefined) return;

        setDeliveryMethodValuesMap((prevMap) => {
            const updatedMap = JSON.parse(JSON.stringify(prevMap));
            for (const key in updatedMap) {
                const config = updatedMap[key];
                if (config.dynamicVariables && config.dynamicVariables[fieldName]) {
                    config.dynamicVariables[fieldName].isValid = validation.isValid;
                }
            }
            return updatedMap;
        });
    };

    const untypedData = useMemo(
        () => mapPropertiesToCascadeDropdownModel(collectedProperties ?? []),
        [collectedProperties],
    );

    const uniqueDynamicVariables = useMemo(() => {
        const allVariables = Object.values(deliveryMethodValuesMap).flatMap((config) => {
            return config.dynamicVariables && config.isEnabled ? Object.values(config.dynamicVariables) : [];
        }) as CJDynamicVariables[];

        const groupedByName = allVariables.reduce(
            (acc, variable) => {
                const key = variable.fieldName;
                if (!acc[key]) {
                    acc[key] = [];
                }
                acc[key].push(variable);
                return acc;
            },
            {} as Record<string, CJDynamicVariables[]>,
        );

        const representativeVariables = Object.values(groupedByName).map((group) => {
            return group.sort((a, b) => {
                if (a.isChecked !== b.isChecked) {
                    return a.isChecked ? -1 : 1;
                }
                const aHasValue = a.value && Object.keys(a.value).length > 0;
                const bHasValue = b.value && Object.keys(b.value).length > 0;
                if (aHasValue !== bHasValue) {
                    return aHasValue ? -1 : 1;
                }
                return 0;
            })[0];
        });

        return representativeVariables;
    }, [deliveryMethodValuesMap]);

    const rowUpdateHandler = (value, deliveryMethodConfig) => {
        setDeliveryMethodValuesMap((prev) => ({
            ...prev,
            [deliveryMethodConfig.deliveryMethodId]: value,
        }));
    };

    const dynamicVariablesUpdateHandler = (dynamicVariablesConfig, deliveryMethodConfig) => {
        setDeliveryMethodValuesMap((prev) => {
            let tmpResult = {};
            if (!isEmpty(dynamicVariablesConfig)) {
                tmpResult = accumulateConfigFromStates(dynamicVariablesConfig, prev);
            }

            return {
                ...prev,
                [deliveryMethodConfig.deliveryMethodId]: {
                    ...prev[deliveryMethodConfig.deliveryMethodId],
                    dynamicVariables: tmpResult,
                },
            };
        });
    };

    const getAvailableDeliveryMethods = () => {
        setIsLoadingDeliveryMethods(true);
        doGetRequest(getAvailableDeliveryMethodsRequest.request, {
            successCallback: (data) => {
                setAvailableDeliveryMethods(data);
                setIsLoadingDeliveryMethods(false);
            },
            errorCallback: () => {
                setIsLoadingDeliveryMethods(false);
            },
        });
    };

    const init = () => {
        getAvailableDeliveryMethods();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    if (isLoadingDeliveryMethods) {
        return <SkeletonLoader isBusy={true} />;
    }

    return (
        <div>
            <DraggableForm
                data={dndSortedConfigData}
                idKey="name"
                rowContainerClassName={styles.rowContainer}
                onChange={handleDraggableFormChange}
                rowRenderer={(props, index, parentKey) => {
                    const { config } = props;

                    return (
                        <CJBestMessageDeliveryMethodRow
                            key={config.deliveryMethodName}
                            config={config}
                            data={deliveryMethodValuesMap[config.deliveryMethodId] || {}}
                            onChange={(data) => rowUpdateHandler(data, config)}
                            getDynamicVariablesUpdate={(value) => dynamicVariablesUpdateHandler(value, config)}
                            setIsLoading={setIsLoadingDynamicVariables}
                            updateDependency={updateDependency}
                            parentKey={parentKey}
                            index={index}
                        />
                    );
                }}
            />

            <div className="crm-template-dynamic-variables-container">
                <SkeletonLoader isBusy={isLoadingDynamicVariables}>
                    {!isLoadingDynamicVariables &&
                        uniqueDynamicVariables.map((item, index) => {
                            return (
                                <DynamicVariableOption
                                    key={`${item.displayNameKey}_${index}`}
                                    label={isNil(item.wfType) ? item.fieldName : t(item.displayNameKey)}
                                    elements={elements}
                                    option={option}
                                    node={node}
                                    data={
                                        isNil(item.wfType)
                                            ? untypedData
                                            : mapPropertiesToCascadeDropdownModel(
                                                  getMatchedProperties(item.wfType, collectedProperties),
                                              )
                                    }
                                    getUpdate={(newValue) => dynamicVariablesChangeHandler(item.fieldName, newValue)}
                                    getValidationUpdate={(validation) =>
                                        dynamicVariablesValidationHandler(item.fieldName, validation)
                                    }
                                    defaultValue={{ isChecked: item.isChecked, value: item.value }}
                                />
                            );
                        })}
                </SkeletonLoader>
            </div>
        </div>
    );
};

export default memo(CJBestMessageSender);
